/**
 * Documentos da Equipe (pacote D).
 *
 * O DONO sobe, vê e remove; o FUNCIONÁRIO vê e baixa. Quem manda é o backend —
 * esconder botão aqui é conveniência, não autorização.
 */
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import React, { useState } from "react";

import {
  useBaixarDocumentoEquipe,
  useDocumentosEquipe,
  useRemoverDocumentoEquipe,
  useSubirDocumentoEquipe,
} from "@/hooks/useDocumentosEquipe";
import usePortalPermissions from "@/hooks/usePortalPermissions";
import type { DocumentoEquipe } from "@/types/portal";

const TAMANHO_MAXIMO = 10 * 1024 * 1024;

function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatarData(iso: string | null): string {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return "—";
  }
}

const PortalDocumentosEquipe: React.FC = () => {
  const { has } = usePortalPermissions();
  const podeGerenciar = has("portal:documentos_equipe:manage");

  const { data, isLoading, isError, refetch } = useDocumentosEquipe();
  const subir = useSubirDocumentoEquipe();
  const remover = useRemoverDocumentoEquipe();
  const baixar = useBaixarDocumentoEquipe();

  const [titulo, setTitulo] = useState("");
  const [categoria, setCategoria] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErroLocal(null);

    if (!titulo.trim()) {
      setErroLocal("Dê um título para a equipe saber o que é o documento.");
      return;
    }
    if (!arquivo) {
      setErroLocal("Escolha um arquivo.");
      return;
    }
    // Barra aqui para o dono não esperar o upload inteiro só para tomar 413.
    if (arquivo.size > TAMANHO_MAXIMO) {
      setErroLocal("Arquivo maior que 10 MB. Comprima o PDF ou divida em partes.");
      return;
    }

    try {
      await subir.mutateAsync({
        titulo,
        categoria: categoria || undefined,
        arquivo,
      });
      setTitulo("");
      setCategoria("");
      setArquivo(null);
      (document.getElementById("arquivo-documento-equipe") as HTMLInputElement | null)?.value &&
        ((document.getElementById("arquivo-documento-equipe") as HTMLInputElement).value = "");
    } catch (err) {
      const detalhe =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        "Não foi possível enviar o documento. Tente de novo.";
      setErroLocal(detalhe);
    }
  };

  const itens: DocumentoEquipe[] = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Documentos da Equipe</h1>
        <p className="text-sm text-muted-foreground">
          {podeGerenciar
            ? "Arquivos que você compartilha com os seus funcionários. Ficam hospedados no SE_WEB."
            : "Arquivos que o responsável compartilhou com a equipe."}
        </p>
      </div>

      {podeGerenciar && (
        <form
          onSubmit={enviar}
          className="rounded-lg border p-4 space-y-3"
          aria-label="Enviar documento"
        >
          <h2 className="font-semibold">Enviar novo documento</h2>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="titulo-documento-equipe" className="text-sm font-medium">
                Título
              </label>
              <input
                id="titulo-documento-equipe"
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                maxLength={300}
                placeholder="Escala de agosto"
              />
            </div>
            <div>
              <label htmlFor="categoria-documento-equipe" className="text-sm font-medium">
                Categoria (opcional)
              </label>
              <input
                id="categoria-documento-equipe"
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                maxLength={120}
                placeholder="Escalas"
              />
            </div>
          </div>

          <div>
            <label htmlFor="arquivo-documento-equipe" className="text-sm font-medium">
              Arquivo (PDF, Word, Excel ou imagem — até 10 MB)
            </label>
            <input
              id="arquivo-documento-equipe"
              type="file"
              className="w-full text-sm"
              accept=".pdf,.docx,.xlsx,.doc,.xls,.jpg,.jpeg,.png"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            />
          </div>

          {erroLocal && (
            <p role="alert" className="text-sm text-red-600">
              {erroLocal}
            </p>
          )}

          <button
            type="submit"
            disabled={subir.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-60"
          >
            <Upload className="h-4 w-4" />
            {subir.isPending ? "Enviando…" : "Enviar documento"}
          </button>
        </form>
      )}

      {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}

      {isError && (
        <div role="alert" className="rounded-lg border p-4">
          <p className="text-sm">Não foi possível carregar os documentos.</p>
          <button onClick={() => refetch()} className="mt-2 text-sm underline" type="button">
            Tentar novamente
          </button>
        </div>
      )}

      {!isLoading && !isError && itens.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            {podeGerenciar
              ? "Você ainda não compartilhou nenhum documento com a equipe."
              : "O responsável ainda não compartilhou documentos."}
          </p>
        </div>
      )}

      {itens.length > 0 && (
        <ul className="space-y-2">
          {itens.map((doc) => (
            <li
              key={doc.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
            >
              <div className="min-w-0">
                <p className="font-medium">{doc.titulo}</p>
                <p className="text-xs text-muted-foreground">
                  {doc.nome_arquivo} · {formatarTamanho(doc.tamanho_bytes)}
                  {doc.categoria ? ` · ${doc.categoria}` : ""}
                </p>
                {podeGerenciar && (
                  <p className="text-xs text-muted-foreground">
                    Último download: {formatarData(doc.ultimo_download_em)}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => baixar.mutate(doc.id)}
                  className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm"
                >
                  <Download className="h-4 w-4" />
                  Baixar
                </button>

                {podeGerenciar && (
                  <button
                    type="button"
                    aria-label={`Remover ${doc.titulo}`}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remover "${doc.titulo}"? A equipe deixa de ver este documento.`
                        )
                      ) {
                        remover.mutate(doc.id);
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remover
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PortalDocumentosEquipe;
